import _ from "lodash";
import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";

import withLoader from "UIComponents/GqlWithLoader";
import { DSProvider } from "@toddle-edu/ds-web";

import {
  getOrganizationGradesQuery,
  getOrganizationSubjectsQuery,
} from "CommonGraphql/Organization/OrganizationQueries";
import {
  getOrganizationGradesFromCache,
  getOrganizationSubjectsFromCache,
} from "CommonGraphql/Organization/OrganizationGraphqlHelpers";

import {
  getAcademicYearsList,
  getCourseTypes,
  getSimplifiedFilterValue,
  getIsAssessmentsOptionsDisabled,
} from "./modules/Utils";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getOrganizationId,
} from "modules/CommonSelectors";

import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";

import LearningCourseSelectionHeader from "./components/LearningCourseSelectionHeader";
import LearningCourseSelectionFeed from "./components/LearningCourseSelectionFeed";

import classes from "./LearningCourseSelection.scss";

const LearningCourseSelection = props => {
  const {
    isCurriculumPYP,
    setSelectedLearningCourse,
    selectedLearningCourse,
    setCourseSearchText,
    courseSearchText,
    grades,
    subjects,
    academicYearList,
    setAcademicYear,
    setCourseType,
    setGrade,
    setSubject,
    academicYear,
    courseType,
    grade,
    subject,
    currentCurriculumProgramId,
  } = props;
  const [totalCount, setTotalCount] = useState(0);
  const [isCoursesLoading, setisCoursesLoading] = useState(true);
  const scrollbarRef = useRef(null);

  const courseTypes = getCourseTypes();

  return (
    <DSProvider dsVersion="2.0">
      <div className={classes.container} ref={scrollbarRef}>
        <LearningCourseSelectionHeader
          courseSearchText={courseSearchText}
          handleClassSearch={setCourseSearchText}
          isCoursesLoading={isCoursesLoading}
          grades={grades}
          subjects={subjects}
          academicYearList={academicYearList}
          courseTypes={courseTypes}
          setAcademicYear={setAcademicYear}
          setCourseType={setCourseType}
          setGrade={setGrade}
          setSubject={setSubject}
          academicYear={academicYear}
          courseType={courseType}
          grade={grade}
          subject={subject}
          isCurriculumPYP={isCurriculumPYP}
          totalCount={totalCount}
        />
        <LearningCourseSelectionFeed
          setTotalCount={setTotalCount}
          isCurriculumPYP={isCurriculumPYP}
          setSelectedLearningCourse={setSelectedLearningCourse}
          selectedLearningCourse={selectedLearningCourse}
          courseSearchText={courseSearchText}
          academicYear={getSimplifiedFilterValue({
            selectedOptions: academicYear,
            allOptions: academicYearList,
          })}
          courseType={getSimplifiedFilterValue({
            selectedOptions: courseType,
            allOptions: courseTypes,
          })}
          grade={getSimplifiedFilterValue({
            selectedOptions: grade,
            allOptions: grades,
          })}
          subject={getSimplifiedFilterValue({
            selectedOptions: subject,
            allOptions: subjects,
          })}
          currentCurriculumProgramId={currentCurriculumProgramId}
          setisCoursesLoading={setisCoursesLoading}
          scrollbarRef={scrollbarRef}
        />
      </div>
    </DSProvider>
  );
};

const mapStateToProps = state => {
  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);
  const organizationId = getOrganizationId(state);
  const curriculumProgramIds = currentCurriculumProgramId
    ? [currentCurriculumProgramId]
    : [];

  const academicYearList = getAcademicYearsList({
    organizationId,
  });

  const hideAssessments = getIsAssessmentsOptionsDisabled({ state });

  const filters = {
    curriculumProgramIds,
  };

  const subjectFilters = {
    curriculumProgramIds,
  };

  if (hideAssessments) {
    subjectFilters.types = ["CAS", "EE"];
  }

  const isCurriculumPYP = currentCurriculumProgramType == CURRICULUM_TYPE_PYP;

  return {
    isData: true,
    isLoading: false,
    isCurriculumPYP,
    currentCurriculumProgramId,
    filters,
    subjectFilters,
    academicYearList,
    organizationId,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getOrganizationSubjectsQuery, {
    name: "getOrganizationSubjects",
    options: ({ subjectFilters, organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: organizationId, filters: subjectFilters },
    }),
    skip: ({ isCurriculumPYP }) => isCurriculumPYP,
    props: ({ getOrganizationSubjects, ownProps: { isLoading, isData } }) => {
      const variables = _.get(getOrganizationSubjects, "variables", {});
      const data = getOrganizationSubjectsFromCache(variables);
      const subjects = _.map(_.get(data, "subjects", []), item => {
        const { name, id } = item;
        return {
          label: name,
          value: id,
        };
      });
      return {
        subjects,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationSubjects.networkStatus) ||
          isLoading,
      };
    },
  }),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    options: ({ organizationId, filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters,
      },
    }),
    props({ getOrganizationGrades, ownProps: { isData, isLoading } }) {
      const variables = _.get(getOrganizationGrades, "variables", {});
      const data = getOrganizationGradesFromCache(variables);

      const grades = _.map(_.get(data, "grades", []), item => {
        const { name, id } = item;
        return {
          label: name,
          value: id,
        };
      });

      return {
        isData: !_.isEmpty(data) && isData,
        grades,
        isLoading:
          _.includes([1, 2], getOrganizationGrades.networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(LearningCourseSelection);
